// Modal that previews + lets the owner share a single rating's note as
// an image. Server-side renders SVG; client converts to PNG via Canvas
// for download / system-share. Square only (1:1) — the story 9:16
// option was retired because almost every modern share destination
// accepts square and the picker was friction nobody used.
//
// Why client-side rasterization: avoids the backend bundling fonts or
// pulling a CGO rasterizer (skia / resvg). SVG is what the backend
// produces; PNG is what social platforms / Save Image expects.

import { useEffect, useMemo, useState } from 'react';
import { sessionStore } from '@/lib/api';
import { friendlyError } from '@/lib/errors';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:8080';

interface Props {
  ratingId: string;
  /** Used in the download filename. */
  slug: string;
  onClose: () => void;
}

export function ShareCardModal({ ratingId, slug, onClose }: Props) {
  const [svg, setSvg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<'png' | 'copy' | 'share' | null>(null);

  // Web Share API with files is mobile-first. Desktop browsers
  // overwhelmingly return false here, so this naturally gates the
  // surface — Share button on mobile, Download button on desktop.
  const canNativeShare = (() => {
    if (typeof navigator === 'undefined') return false;
    const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
    if (!nav.canShare) return false;
    try {
      const probe = new File([new Blob(['x'], { type: 'image/png' })], 'probe.png', { type: 'image/png' });
      return nav.canShare({ files: [probe] });
    } catch {
      return false;
    }
  })();

  // Fetch the SVG once on mount. Auth header carries the access token
  // — the endpoint is owner-only so we have to use fetch rather than
  // rendering <img src=...> directly (img tags can't carry
  // Authorization headers). Square (1:1) by default on the backend.
  useEffect(() => {
    let cancelled = false;
    setSvg(null);
    setErr(null);
    const token = sessionStore.getAccess();
    fetch(`${API_BASE}/profiles/me/ratings/${ratingId}/share.svg`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
        return r.text();
      })
      .then((text) => { if (!cancelled) setSvg(text); })
      .catch((e) => { if (!cancelled) setErr(String(e)); });
    return () => { cancelled = true; };
  }, [ratingId]);

  // Data URL of the SVG for the <img> preview AND the canvas raster.
  // Use a Blob-backed object URL so SVGs with @font-face imports load
  // their fonts (data: URIs can't fetch external resources).
  const svgURL = useMemo(() => {
    if (!svg) return null;
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    return URL.createObjectURL(blob);
  }, [svg]);
  useEffect(() => () => { if (svgURL) URL.revokeObjectURL(svgURL); }, [svgURL]);

  // Shared rasterization step — turns the loaded SVG into a PNG Blob
  // at the format's native resolution. Both Download PNG (saves to
  // disk) and Share image (hands to navigator.share) consume it.
  const rasterizeToBlob = async (): Promise<Blob> => {
    if (!svgURL) throw new Error('SVG not loaded.');
    // Wait on fonts to load so the rasterized PNG includes our brand
    // typefaces instead of system fallback. Without this, the canvas
    // can rasterize before @font-face resolves and the PNG ends up
    // with Times New Roman.
    try { await (document as any).fonts?.ready; } catch { /* ignore */ }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    const loaded = new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Could not load SVG into <img> for rasterization.'));
    });
    img.src = svgURL;
    await loaded;

    const [w, h] = [1080, 1080]; // square only
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable.');
    ctx.drawImage(img, 0, 0, w, h);

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) reject(new Error('Could not encode PNG.'));
        else resolve(blob);
      }, 'image/png');
    });
  };

  const downloadPNG = async () => {
    setBusy('png');
    try {
      const blob = await rasterizeToBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wydm-${slug || 'rating'}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setErr(friendlyError(e, 'ShareCardModal'));
    } finally {
      setBusy(null);
    }
  };

  // Native Web Share — mobile-only path. Hands the PNG file to the OS
  // share sheet which the user routes to IG / WhatsApp / Messages.
  // AbortError fires when the user dismisses the sheet — silent.
  const shareImage = async () => {
    setBusy('share');
    try {
      const blob = await rasterizeToBlob();
      const file = new File([blob], `wydm-${slug || 'rating'}.png`, { type: 'image/png' });
      const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
      if (!nav.share) throw new Error('Sharing not available.');
      await nav.share({
        files: [file],
        title: 'My WYDM rating note',
        text: 'A note someone left on my WYDM profile.',
      });
    } catch (e) {
      const name = (e as { name?: string }).name;
      if (name !== 'AbortError') setErr(friendlyError(e, 'ShareCardModal'));
    } finally {
      setBusy(null);
    }
  };

  const copySVGUrl = async () => {
    setBusy('copy');
    try {
      // Direct copy of the SVG payload itself — not a public URL, since
      // the endpoint is owner-only. The pasted SVG is portable + works
      // on any platform that accepts inline SVG.
      if (svg) await navigator.clipboard.writeText(svg);
    } finally {
      setBusy(null);
      // Tiny pulse — re-flag busy so the button visibly responded.
      setTimeout(() => setBusy(null), 500);
    }
  };

  return (
    <div style={s.scrim} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <div className="mono" style={s.eyebrow}>SHARE CARD</div>
          <button type="button" onClick={onClose} style={s.closeBtn} aria-label="Close">×</button>
        </div>

        {/* Preview — square only. Story (9:16) was retired; the
            picker added friction without surfacing a destination that
            doesn't already accept square. */}
        <div
          style={{
            ...s.previewBox,
            aspectRatio: '1 / 1',
            maxWidth: 380,
          }}
        >
          {err ? (
            <div style={s.errorBox}>{err}</div>
          ) : svgURL ? (
            <img src={svgURL} alt="Share preview" style={s.previewImg} />
          ) : (
            <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
          )}
        </div>

        {/* Actions — Share is the primary action on mobile (where the
            native share sheet exists); Download is the desktop
            primary. Detection runs once at mount via canNativeShare. */}
        <div style={s.actionsRow}>
          {canNativeShare ? (
            <button
              type="button"
              onClick={shareImage}
              disabled={!svg || busy === 'share'}
              style={s.primaryBtn}
            >
              {busy === 'share' ? 'Opening…' : 'Share'}
            </button>
          ) : (
            <button
              type="button"
              onClick={downloadPNG}
              disabled={!svg || busy === 'png'}
              style={s.primaryBtn}
            >
              {busy === 'png' ? 'Exporting…' : 'Download PNG'}
            </button>
          )}
          <button
            type="button"
            onClick={copySVGUrl}
            disabled={!svg || busy === 'copy'}
            style={s.secondaryBtn}
          >
            {busy === 'copy' ? 'Copied' : 'Copy SVG'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── styles ─────────────────────────────────────────────────────────────────

const s = {
  scrim: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(43, 29, 20, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modal: {
    background: 'var(--bg)',
    borderRadius: 22,
    padding: 24,
    width: '100%',
    maxWidth: 460,
    boxShadow: 'var(--shadow-lift)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  eyebrow: {
    fontSize: 11, color: 'var(--coral)', letterSpacing: 2, textTransform: 'uppercase' as const,
  },
  closeBtn: {
    width: 32, height: 32, borderRadius: 32,
    background: 'var(--surface)', color: 'var(--ink)', border: 'none',
    fontSize: 22, lineHeight: 1, cursor: 'pointer',
  },
  previewBox: {
    margin: '0 auto 20px',
    background: 'var(--surface-2)',
    borderRadius: 14,
    overflow: 'hidden' as const,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  previewImg: { width: '100%', height: '100%', display: 'block', objectFit: 'cover' as const },
  errorBox: { color: '#a8331f', fontSize: 12, padding: 14, textAlign: 'center' as const },
  actionsRow: { display: 'flex', gap: 8 },
  primaryBtn: {
    flex: 1,
    padding: '12px 20px',
    background: 'var(--coral)', color: '#fff',
    border: 'none', borderRadius: 100,
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
    fontFamily: 'var(--font-ui)',
  },
  secondaryBtn: {
    flex: 1,
    padding: '12px 20px',
    background: 'transparent', color: 'var(--ink)',
    border: '1.5px solid var(--line-strong)', borderRadius: 100,
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
    fontFamily: 'var(--font-ui)',
  },
};
